import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, ArrowRight, XCircle, Mail, Lock, Building2, User, Rocket, LifeBuoy, ShieldCheck, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { getPaymentStatus, provisionWorkspace, resyncBilling } from "@/lib/api";
import { SITE } from "@/lib/site";
import { money } from "@/lib/pricing";
import { cn } from "@/lib/utils";
import { Container, Kicker, usePageTitle } from "@/components/ui-kit";

const Shell = ({ children, testId }) => (
  <section className="relative overflow-hidden min-h-[70vh] flex items-center" data-testid={testId}>
    <div className="absolute inset-0 grid-bg opacity-40" />
    <div className="orb h-[420px] w-[420px] -top-20 right-[-120px] bg-mk-lime/15" />
    <Container className="relative py-24 max-w-3xl">{children}</Container>
  </section>
);

const Field = ({ label, icon: I, children }) => (
  <label className="block">
    <span className="text-xs font-medium text-mk-dim flex items-center gap-1.5">{I && <I className="h-3.5 w-3.5" />}{label}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);

const inputCls = "w-full rounded-lg border border-mk-border bg-mk-bg px-3.5 py-2.5 text-sm text-mk-text placeholder:text-mk-muted focus:outline-none focus:border-mk-lime/60 focus:ring-2 focus:ring-mk-lime/20";

const PlanSummary = ({ data }) => {
  const plan = data.plan;
  if (!plan) return null;
  return (
    <div className="rounded-xl border border-mk-border bg-mk-bg p-5 text-sm" data-testid="payment-summary">
      <div className="flex justify-between"><span className="text-mk-dim">Plan</span><span className="font-semibold text-mk-text" data-testid="payment-plan-name">{plan.preset_name}</span></div>
      {plan.seats && <div className="mt-2 flex justify-between"><span className="text-mk-dim">Users</span><span className="font-mono text-mk-text">{plan.seats}</span></div>}
      {plan.addons?.length > 0 && <div className="mt-2 flex justify-between gap-6"><span className="text-mk-dim">Add-ons</span><span className="font-mono text-mk-text text-right">{plan.addons.length} selected</span></div>}
      <div className="mt-2 flex justify-between"><span className="text-mk-dim">{plan.billing === "one-time" ? "Paid" : plan.billing === "annual" ? "Per year after trial" : "Per month after trial"}</span><span className="font-mono text-mk-lime" data-testid="payment-amount">{money(data.amount)}</span></div>
    </div>
  );
};

const ProvisionForm = ({ sessionId, data, onDone }) => {
  const [form, setForm] = useState({ company_name: data.company_name || "", name: data.full_name || "", password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) return toast.error("Password must be at least 8 characters");
    if (form.password !== form.confirm) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      const res = await provisionWorkspace({ session_id: sessionId, password: form.password, company_name: form.company_name.trim(), name: form.name.trim() });
      onDone(res);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't create the workspace — please try again");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="mt-6 rounded-2xl border border-mk-lime/30 bg-mk-lime/[0.04] p-5 sm:p-6" data-testid="provision-form">
      <div className="flex items-center gap-2 text-mk-lime text-sm font-semibold"><Rocket className="h-4 w-4" />Create your workspace — 30 seconds</div>
      <p className="mt-1 text-xs text-mk-dim">We'll spin up <b className="text-mk-text">{form.company_name || "your company"}</b> on fixlr.app with this plan attached. Choose the password you'll sign in with.</p>
      <div className="mt-5 grid sm:grid-cols-2 gap-4">
        <Field label="Company / workspace name" icon={Building2}><input required minLength={2} value={form.company_name} onChange={set("company_name")} className={inputCls} placeholder="Belmont Estates" data-testid="provision-company-input" /></Field>
        <Field label="Your full name" icon={User}><input required minLength={2} value={form.name} onChange={set("name")} className={inputCls} placeholder="Chris Harper" data-testid="provision-name-input" /></Field>
        <Field label="Admin email (from checkout)" icon={Mail}><input value={data.customer_email || ""} readOnly className={`${inputCls} opacity-70 cursor-not-allowed`} data-testid="provision-email-input" /></Field>
        <div className="hidden sm:block" />
        <Field label="Password" icon={Lock}><input required type="password" minLength={8} value={form.password} onChange={set("password")} className={inputCls} placeholder="Minimum 8 characters" data-testid="provision-password-input" /></Field>
        <Field label="Confirm password" icon={Lock}><input required type="password" minLength={8} value={form.confirm} onChange={set("confirm")} className={inputCls} placeholder="Repeat password" data-testid="provision-confirm-input" /></Field>
      </div>
      <button type="submit" disabled={busy} className="btn-lime mt-6 w-full sm:w-auto disabled:opacity-70" data-testid="provision-submit-btn">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}{busy ? "Creating your workspace…" : "Create workspace"}
      </button>
      <p className="mt-3 text-[11px] text-mk-muted">By continuing you agree to the <Link to="/terms" className="underline">Terms</Link>. Your admin login is {data.customer_email}.</p>
    </form>
  );
};

const SyncBadge = ({ data, sessionId }) => {
  const [state, setState] = useState(data.billing_sync);
  const [busy, setBusy] = useState(false);
  const retry = async () => {
    setBusy(true);
    try { const d = await resyncBilling(sessionId); setState(d.billing_sync); if (d.billing_sync === "synced") toast.success("Billing synced with fixlr.app"); else toast.message("Sync still pending — the team has been notified"); }
    catch { toast.error("Couldn't reach the sync service"); }
    finally { setBusy(false); }
  };
  const synced = state === "synced";
  return (
    <div className={cn("mt-6 inline-flex flex-wrap items-center gap-2 rounded-xl border px-3 py-2 text-xs", synced ? "border-mk-lime/40 bg-mk-lime/10 text-mk-lime" : "border-mk-border bg-mk-bg text-mk-dim")} data-testid="payment-billing-sync">
      {synced ? <ShieldCheck className="h-3.5 w-3.5" /> : <RefreshCw className={cn("h-3.5 w-3.5", busy && "animate-spin")} />}
      <span data-testid="payment-billing-sync-state">{synced ? "Plan marked active in your workspace — no trial banner." : state === "failed" ? "Plan activation couldn't reach your workspace yet — our team has been alerted and will activate it manually." : "Plan activation is syncing to your workspace; if you see a trial badge, it clears once sync completes."}</span>
      {!synced && <button type="button" onClick={retry} disabled={busy} className="font-semibold text-mk-text hover:text-mk-lime transition-colors" data-testid="payment-billing-resync-btn">Retry now</button>}
    </div>
  );
};

const Ready = ({ data, sessionId }) => (
  <div className="rounded-3xl border border-mk-lime/40 bg-mk-surface p-8 sm:p-12 glow-lime" data-testid="payment-provisioned">
    <CheckCircle2 className="h-12 w-12 text-mk-lime" />
    <Kicker className="mt-6">Workspace live</Kicker>
    <h1 className="mt-3 font-display text-3xl sm:text-4xl font-bold text-mk-text"><span className="text-mk-lime">{data.company_name}</span> is ready.</h1>
    <p className="mt-4 text-mk-dim leading-relaxed">Your plan is attached and your admin account is <b className="text-mk-text">{data.customer_email}</b>. Sign in with the password you just set — we've also emailed you the details and a 15-minute setup checklist.</p>
    <SyncBadge data={data} sessionId={sessionId} />
    <div className="mt-8 flex flex-wrap gap-3">
      <a href={data.login_url || SITE.loginUrl} target="_blank" rel="noreferrer" className="btn-lime" data-testid="payment-signin-btn">Sign in to your workspace <ArrowRight className="h-4 w-4" /></a>
      <Link to="/contact" className="btn-ghost" data-testid="payment-onboarding-btn"><Mail className="h-4 w-4" />Book onboarding call</Link>
    </div>
    <p className="mt-4 font-mono text-[11px] text-mk-muted">Ref: {sessionId}</p>
  </div>
);

const Manual = ({ data, sessionId, reason }) => (
  <div className="rounded-3xl border border-mk-blue/40 bg-mk-surface p-8 sm:p-12" data-testid="payment-manual">
    <LifeBuoy className="h-12 w-12 text-[#8db6ff]" />
    <Kicker tone="blue" className="mt-6">Payment confirmed</Kicker>
    <h1 className="mt-3 font-display text-3xl sm:text-4xl font-bold text-mk-text">We're setting up {data.company_name || "your workspace"} by hand.</h1>
    <p className="mt-4 text-mk-dim leading-relaxed">Automatic setup hit a snag{reason ? ` (${reason})` : ""}, so the team has been notified and will create your workspace within one business day — usually much sooner. You'll get sign-in details at <b className="text-mk-text">{data.customer_email}</b>.</p>
    <div className="mt-8 flex flex-wrap gap-3">
      <Link to="/contact" className="btn-blue" data-testid="payment-manual-contact-btn">Talk to the team <ArrowRight className="h-4 w-4" /></Link>
      <a href={SITE.signupUrl} target="_blank" rel="noreferrer" className="btn-ghost">Explore the sandbox meanwhile</a>
    </div>
    <p className="mt-4 font-mono text-[11px] text-mk-muted">Ref: {sessionId}</p>
  </div>
);

export function PaymentSuccess() {
  usePageTitle("Payment complete");
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", data: null });
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    if (!sessionId) { setState({ status: "missing" }); return; }
    let attempts = 0, timer;
    const poll = async () => {
      try {
        const d = await getPaymentStatus(sessionId);
        if (d.payment_status === "paid") {
          setState({ status: d.provisioned ? "provisioned" : d.provision_status === "manual" ? "manual" : "paid", data: d, reason: d.provision_error });
          return;
        }
        if (["failed", "expired"].includes(d.payment_status)) { setState({ status: "failed", data: d }); return; }
      } catch (e) { /* keep polling */ }
      if (++attempts < 8) timer = setTimeout(poll, 2000); else setState({ status: "timeout" });
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, retry]);

  const onProvisioned = (res) => {
    if (res.status === "provisioned" || res.status === "already_provisioned") { toast.success("Workspace created"); setState({ status: "provisioned", data: res }); }
    else setState({ status: "manual", data: res, reason: res.reason });
  };

  return (
    <Shell testId="payment-success-page">
      {state.status === "checking" && (
        <div className="text-center" data-testid="payment-checking">
          <Loader2 className="mx-auto h-10 w-10 text-mk-lime animate-spin" />
          <h1 className="mt-6 font-display text-3xl font-bold text-mk-text">Confirming your payment…</h1>
          <p className="mt-2 text-mk-dim">This usually takes a couple of seconds.</p>
        </div>
      )}
      {state.status === "paid" && (
        <div className="rounded-3xl border border-mk-lime/40 bg-mk-surface p-8 sm:p-12 glow-lime" data-testid="payment-paid">
          <CheckCircle2 className="h-12 w-12 text-mk-lime" />
          <Kicker className="mt-6">Payment complete</Kicker>
          <h1 className="mt-3 font-display text-3xl sm:text-4xl font-bold text-mk-text">You're in. One last step.</h1>
          <div className="mt-6"><PlanSummary data={state.data} /></div>
          <ProvisionForm sessionId={sessionId} data={state.data} onDone={onProvisioned} />
          <p className="mt-4 font-mono text-[11px] text-mk-muted">Ref: {sessionId}</p>
        </div>
      )}
      {state.status === "provisioned" && <Ready data={state.data} sessionId={sessionId} />}
      {state.status === "manual" && <Manual data={state.data} sessionId={sessionId} reason={state.reason} />}
      {["failed", "timeout", "missing"].includes(state.status) && (
        <div className="rounded-3xl border border-mk-border bg-mk-surface p-8 sm:p-12" data-testid="payment-pending">
          <XCircle className="h-12 w-12 text-amber-400" />
          <h1 className="mt-6 font-display text-3xl font-bold text-mk-text">{state.status === "failed" ? "Payment didn't go through" : "Still processing"}</h1>
          <p className="mt-3 text-mk-dim leading-relaxed">{state.status === "failed" ? "Your card wasn't charged. You can try again from the pricing page." : "We haven't received confirmation from Stripe yet. If you completed payment, you'll get an email shortly — otherwise reach us at " + SITE.email + "."}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            {state.status === "timeout" && <button type="button" onClick={() => { setState({ status: "checking", data: null }); setRetry((r) => r + 1); }} className="btn-lime" data-testid="payment-check-again-btn">Check again</button>}
            <Link to="/pricing" className={state.status === "timeout" ? "btn-ghost" : "btn-lime"} data-testid="payment-retry-btn">Back to pricing</Link>
            <Link to="/contact" className="btn-ghost">Contact sales</Link>
          </div>
        </div>
      )}
    </Shell>
  );
}

export function PaymentCancel() {
  usePageTitle("Checkout cancelled");
  return (
    <Shell testId="payment-cancel-page">
      <div className="rounded-3xl border border-mk-border bg-mk-surface p-8 sm:p-12">
        <Kicker>Checkout cancelled</Kicker>
        <h1 className="mt-3 font-display text-3xl sm:text-4xl font-bold text-mk-text">No charge was made.</h1>
        <p className="mt-4 text-mk-dim leading-relaxed">Your plan is still saved in the builder. If pricing questions held you back, the team can walk you through it in 15 minutes — or start in the free sandbox with no card at all.</p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/pricing#build" className="btn-lime" data-testid="payment-cancel-back-btn">Back to plan builder <ArrowRight className="h-4 w-4" /></Link>
          <a href={SITE.signupUrl} target="_blank" rel="noreferrer" className="btn-ghost" data-testid="payment-cancel-sandbox-btn">Try the free sandbox</a>
          <Link to="/contact" className="btn-ghost">Talk to sales</Link>
        </div>
      </div>
    </Shell>
  );
}
